import { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, Plus, Search, Users, TrendingUp, DollarSign,
  Mail, Trash2, Power, Copy, ShieldCheck, Loader2,
  Sparkles, Package, Instagram, Youtube, Globe,
} from 'lucide-react';
import { api, InfluencerWithStatus, CreateInfluencerData, API_BASE, proxyImageUrl } from '../api';
import { PageHeader, StatusBadge, useToast, ToastContainer } from '../components/ui';
import { useDemo } from '../context/DemoContext';
import { DEMO_INFLUENCERS_ENRICHED } from '../demo/data';

type Filter = 'all' | 'active' | 'needs_setup' | 'inactive';

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
    : `$${n.toFixed(0)}`;

const fmtFollowers = (n: number | null | undefined) => {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

function PlatformIcon({ platform }: { platform: string | null | undefined }) {
  if (!platform) return <Globe className="w-3.5 h-3.5" />;
  switch (platform.toLowerCase()) {
    case 'instagram': return <Instagram className="w-3.5 h-3.5" />;
    case 'youtube': return <Youtube className="w-3.5 h-3.5" />;
    case 'tiktok': return (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.93 2.93 0 0 1 .88.13v-3.5a6.37 6.37 0 0 0-.88-.07 6.33 6.33 0 0 0 0 12.67 6.33 6.33 0 0 0 6.33-6.33V8.55a8.22 8.22 0 0 0 3.77 1v-2.86z" />
      </svg>
    );
    default: return <Globe className="w-3.5 h-3.5" />;
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Modal ──────────────────────────────────────────────────────────────────

function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="card p-6 w-full max-w-lg relative z-10 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-white">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2 text-white/40 hover:text-white/80">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Match result inline viewer ─────────────────────────────────────────────

interface InlineMatchResult {
  top_products: Array<{
    rank: number;
    title: string;
    vertical: string;
    score_pct: number;
    primary_image_url: string | null;
    tags: string[];
  }>;
  creator_overview: {
    handle: string;
    platform: string;
    followers: number;
    engagement_rate: number;
    primary_category: string;
  };
  summary?: string;
}

function InlineMatchReport({ result }: { result: InlineMatchResult }) {
  return (
    <div className="space-y-3">
      {result.summary && (
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
          {result.summary}
        </p>
      )}
      <div className="space-y-2">
        {result.top_products.map((p, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5"
            style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 3 }}>
            <span className="text-xs font-mono w-5 text-center shrink-0" style={{ color: '#FFD400' }}>
              #{p.rank}
            </span>
            <div className="w-8 h-8 rounded shrink-0 overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              {p.primary_image_url ? (
                <img src={proxyImageUrl(p.primary_image_url) ?? undefined} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <Package className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.15)' }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/80 truncate">{p.title}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] px-1.5 py-0.5 capitalize"
                  style={{ backgroundColor: 'rgba(167,139,250,0.1)', color: '#a78bfa', borderRadius: 2 }}>
                  {p.vertical}
                </span>
                {(p.tags ?? []).slice(0, 3).map(t => (
                  <span key={t} className="text-[10px] px-1 py-0.5"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)', borderRadius: 2 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-semibold" style={{ color: '#FFD400' }}>{p.score_pct}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Expanded influencer detail pane ────────────────────────────────────────

function ExpandedInfluencerPane({ inf, isDemoMode }: {
  inf: InfluencerWithStatus;
  isDemoMode: boolean;
}) {
  const [matchResult, setMatchResult] = useState<InlineMatchResult | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState('');

  const runMatch = useCallback(async () => {
    if (isDemoMode) {
      setMatchResult({
        top_products: [
          { rank: 1, title: 'Premium Bamboo Tote Bag', vertical: 'accessories', score_pct: 92, primary_image_url: null, tags: ['eco', 'sustainable', 'tote'] },
          { rank: 2, title: 'Organic Cotton Hoodie', vertical: 'apparel', score_pct: 87, primary_image_url: null, tags: ['cotton', 'streetwear'] },
          { rank: 3, title: 'Minimalist Phone Stand', vertical: 'accessories', score_pct: 81, primary_image_url: null, tags: ['tech', 'minimal'] },
          { rank: 4, title: 'Recycled Denim Crossbody', vertical: 'accessories', score_pct: 78, primary_image_url: null, tags: ['denim', 'upcycled'] },
          { rank: 5, title: 'Ceramic Tumbler Set', vertical: 'home', score_pct: 74, primary_image_url: null, tags: ['home', 'ceramic'] },
        ],
        creator_overview: {
          handle: inf.slug,
          platform: inf.primary_platform || 'tiktok',
          followers: inf.follower_count || 100_000,
          engagement_rate: inf.engagement_rate || 0.045,
          primary_category: inf.industry || 'lifestyle',
        },
        summary: `${inf.stage_name} is a strong fit for sustainable lifestyle and fashion products. Their audience demographics and engagement patterns suggest high conversion potential in accessories and apparel verticals.`,
      });
      return;
    }

    setMatchLoading(true);
    setMatchError('');
    try {
      const handle = inf.slug || inf.influencer_id;
      const platform = inf.primary_platform || 'tiktok';
      const resp = await fetch(`${API_BASE}/admin/creator-match/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ platform, handle }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as any).error || `HTTP ${resp.status}`);
      }
      const data = await resp.json() as any;
      setMatchResult({
        top_products: (data.top_products || []).map((p: any, i: number) => ({
          rank: i + 1,
          title: p.title,
          vertical: p.vertical,
          score_pct: Math.round((p.score ?? p.score_pct ?? 0) * (p.score < 1 ? 100 : 1)),
          primary_image_url: p.primary_image_url,
          tags: p.tags || [],
        })),
        creator_overview: {
          handle: data.creator?.handle || handle,
          platform: data.creator?.platform || platform,
          followers: data.creator?.followers || inf.follower_count || 0,
          engagement_rate: data.creator?.engagement_rate || inf.engagement_rate || 0,
          primary_category: data.creator?.primary_category || inf.industry || '',
        },
        summary: data.assumptions?.summary_text,
      });
    } catch (err) {
      setMatchError(err instanceof Error ? err.message : 'Failed to run match');
    } finally {
      setMatchLoading(false);
    }
  }, [inf, isDemoMode]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-1">
      {/* Left: environment/technical details */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
          System Details
        </h4>
        <div className="space-y-1.5">
          {[
            { label: 'Storefront ID', value: String(inf.storefront_id) },
            { label: 'Influencer ID', value: inf.influencer_id },
            { label: 'Slug', value: inf.slug },
            { label: 'Email', value: inf.email || inf.user_email || '—' },
            { label: 'Created', value: formatDate(inf.created_at) },
            { label: 'Login Status', value: inf.has_user && inf.has_password ? 'Active' : inf.has_user ? 'Invited' : 'No Account' },
          ].map(r => (
            <div key={r.label} className="flex items-center gap-3 text-sm">
              <span className="w-28 shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }}>{r.label}</span>
              <span className="text-white/70 font-mono text-xs">{r.value}</span>
            </div>
          ))}
        </div>
        {inf.bio && (
          <p className="text-sm mt-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {inf.bio}
          </p>
        )}
        {inf.social_handles && Object.keys(inf.social_handles).length > 0 && (
          <div className="mt-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Social Handles
            </h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(inf.social_handles).map(([platform, handle]) => (
                <span key={platform} className="flex items-center gap-1 text-xs px-2 py-1"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 2, color: 'rgba(255,255,255,0.5)' }}>
                  <PlatformIcon platform={platform} />
                  @{handle as string}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: Creator Match */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Creator Match — Product Recommendations
          </h4>
        </div>
        {!matchResult && !matchLoading && (
          <button onClick={runMatch} className="btn-primary text-sm flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Run Creator Match
          </button>
        )}
        {matchLoading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing creator profile...
          </div>
        )}
        {matchError && (
          <p className="text-sm text-[#f87171]">{matchError}</p>
        )}
        {matchResult && <InlineMatchReport result={matchResult} />}
      </div>
    </div>
  );
}

// ── Influencer Card ────────────────────────────────────────────────────────

function InfluencerCard({ inf, expanded, onToggle, onManage, onToggleStatus, onDelete, isDemoMode }: {
  inf: InfluencerWithStatus;
  expanded: boolean;
  onToggle: () => void;
  onManage: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  isDemoMode: boolean;
}) {
  const [imgError, setImgError] = useState(false);
  const initials = inf.stage_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{
      backgroundColor: expanded ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
      border: `1px solid ${expanded ? 'rgba(255,212,0,0.12)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 4,
      transition: 'all 0.15s ease',
    }}>
      {/* Main row */}
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none" onClick={onToggle}>
        {/* Avatar */}
        <div className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: 'rgba(255,212,0,0.1)', border: '2px solid rgba(255,212,0,0.15)' }}>
          {inf.profile_image_url && !imgError ? (
            <img src={inf.profile_image_url} alt={inf.stage_name}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
              referrerPolicy="no-referrer" />
          ) : (
            <span className="text-sm font-bold" style={{ color: '#FFD400' }}>{initials}</span>
          )}
        </div>

        {/* Name + handle + platform */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">{inf.stage_name}</span>
            {inf.primary_platform && (
              <span style={{ color: 'rgba(255,255,255,0.3)' }}><PlatformIcon platform={inf.primary_platform} /></span>
            )}
            {inf.secondary_platforms?.map(p => (
              <span key={p} style={{ color: 'rgba(255,255,255,0.15)' }}><PlatformIcon platform={p} /></span>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>@{inf.slug}</span>
            {inf.industry && (
              <span className="text-[10px] px-1.5 py-0.5 capitalize"
                style={{ backgroundColor: 'rgba(167,139,250,0.1)', color: '#a78bfa', borderRadius: 2 }}>
                {inf.industry}
              </span>
            )}
          </div>
        </div>

        {/* Followers */}
        <div className="hidden md:block text-right w-20 shrink-0">
          <p className="text-sm font-semibold text-white">{fmtFollowers(inf.follower_count)}</p>
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>followers</p>
        </div>

        {/* Engagement */}
        <div className="hidden lg:block text-right w-16 shrink-0">
          <p className="text-sm font-semibold text-white">
            {inf.engagement_rate != null ? `${(inf.engagement_rate * 100).toFixed(1)}%` : '—'}
          </p>
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>engagement</p>
        </div>

        {/* GMV */}
        <div className="text-right w-24 shrink-0">
          <p className="text-sm font-semibold" style={{ color: '#4ade80' }}>
            {fmt(inf.gmv ?? 0)}
          </p>
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
            {inf.total_orders ?? 0} orders
          </p>
        </div>

        {/* Status badge */}
        <div className="w-16 shrink-0">
          <StatusBadge variant={inf.status === 'active' ? 'success' : inf.status === 'inactive' ? 'warning' : 'neutral'}>
            {inf.status}
          </StatusBadge>
        </div>

        {/* Expand chevron */}
        <div className="shrink-0 w-5" style={{ color: 'rgba(255,255,255,0.2)' }}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-5 pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          {/* Quick actions row */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={onManage} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors"
              style={{ backgroundColor: 'rgba(255,212,0,0.06)', border: '1px solid rgba(255,212,0,0.12)', color: '#FFD400' }}>
              <Mail className="w-3 h-3" /> Manage Login
            </button>
            <button onClick={onToggleStatus} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
              <Power className="w-3 h-3" /> {inf.status === 'active' ? 'Deactivate' : 'Activate'}
            </button>
            <button onClick={onDelete} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors"
              style={{ backgroundColor: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.12)', color: '#f87171' }}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>

          <ExpandedInfluencerPane inf={inf} isDemoMode={isDemoMode} />
        </div>
      )}
    </div>
  );
}

// ── Main page component ────────────────────────────────────────────────────

export default function AdminInfluencers() {
  const { isDemoMode } = useDemo();
  const { addToast, toasts, removeToast } = useToast();

  const [influencers, setInfluencers] = useState<InfluencerWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<CreateInfluencerData>({
    first_name: '', last_name: '', stage_name: '', influencer_id: '', slug: '', email: '', status: 'active',
  });
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Manage login modal
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<InfluencerWithStatus | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState('');
  const [manageSuccess, setManageSuccess] = useState('');
  const [inviteResult, setInviteResult] = useState<{ url?: string; expiresAt: string } | null>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [emailEdited, setEmailEdited] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [influencerToDelete, setInfluencerToDelete] = useState<InfluencerWithStatus | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => { loadInfluencers(); }, [isDemoMode]);

  const loadInfluencers = async () => {
    if (isDemoMode) {
      setInfluencers(DEMO_INFLUENCERS_ENRICHED as unknown as InfluencerWithStatus[]);
      setLoading(false);
      return;
    }
    try {
      const { influencers: data } = await api.getInfluencers();
      setInfluencers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load influencers');
    } finally {
      setLoading(false);
    }
  };

  const filtered = influencers
    .filter(inf => {
      if (filter === 'active') return inf.status === 'active';
      if (filter === 'needs_setup') return !inf.has_user || !inf.has_password;
      if (filter === 'inactive') return inf.status === 'inactive';
      return true;
    })
    .filter(inf => {
      if (!search) return true;
      const q = search.toLowerCase();
      return inf.stage_name.toLowerCase().includes(q)
        || inf.slug.toLowerCase().includes(q)
        || (inf.industry || '').toLowerCase().includes(q)
        || (inf.first_name + ' ' + inf.last_name).toLowerCase().includes(q);
    });

  // Stats
  const totalInfluencers = influencers.length;
  const activeInfluencers = influencers.filter(i => i.status === 'active').length;
  const totalGmv = influencers.reduce((acc, i) => acc + (i.gmv ?? 0), 0);
  const totalFollowers = influencers.reduce((acc, i) => acc + (i.follower_count ?? 0), 0);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCreateInfluencer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      await api.createInfluencer(formData);
      setShowCreateModal(false);
      setFormData({ first_name: '', last_name: '', stage_name: '', influencer_id: '', slug: '', email: '', status: 'active' });
      setIdManuallyEdited(false);
      setSlugManuallyEdited(false);
      addToast('Influencer created', 'success');
      loadInfluencers();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create influencer');
    } finally {
      setFormLoading(false);
    }
  };

  const openManageModal = (inf: InfluencerWithStatus) => {
    setSelectedInfluencer(inf);
    setLoginEmail(inf.user_email || inf.email || '');
    setManageError(''); setManageSuccess(''); setInviteResult(null); setResetResult(null); setEmailEdited(false);
    setShowManageModal(true);
  };

  const handleSendInvite = async () => {
    if (!selectedInfluencer || !loginEmail) return;
    setManageLoading(true); setManageError(''); setManageSuccess('');
    try {
      const result = await api.sendInfluencerInvite(selectedInfluencer.id, loginEmail);
      setInviteResult({ url: result.inviteUrl, expiresAt: result.expiresAt });
      setManageSuccess(result.message);
      setEmailEdited(false);
      loadInfluencers();
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setManageLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!selectedInfluencer || !loginEmail) return;
    setManageLoading(true); setManageError(''); setManageSuccess('');
    try {
      const result = await api.updateInfluencerEmail(selectedInfluencer.id, loginEmail);
      setManageSuccess(result.message);
      setEmailEdited(false);
      loadInfluencers();
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setManageLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedInfluencer) return;
    setManageLoading(true); setManageError(''); setManageSuccess('');
    try {
      const result = await api.resetInfluencerPassword(selectedInfluencer.id);
      setResetResult(result.resetUrl || result.message);
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setManageLoading(false);
    }
  };

  const handleToggleStatus = async (inf: InfluencerWithStatus) => {
    try {
      const newStatus = inf.status === 'active' ? 'inactive' : 'active';
      await api.updateInfluencer(inf.id, { status: newStatus });
      addToast(`${inf.stage_name} ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
      loadInfluencers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const openDeleteModal = (inf: InfluencerWithStatus) => {
    setInfluencerToDelete(inf);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDeleteInfluencer = async () => {
    if (!influencerToDelete) return;
    setDeleteLoading(true); setDeleteError('');
    try {
      await api.deleteInfluencer(influencerToDelete.id);
      setShowDeleteModal(false);
      setInfluencerToDelete(null);
      addToast(`${influencerToDelete.stage_name} deleted`, 'success');
      loadInfluencers();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete influencer');
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('Copied to clipboard', 'success');
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <PageHeader title="Influencers" subtitle="Manage creator accounts, view performance, and run product matching"
          actions={
            <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add Influencer
            </button>
          }
        />

        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total Creators', value: String(totalInfluencers), icon: <Users className="w-4 h-4" />, color: '#FFD400' },
            { label: 'Active', value: String(activeInfluencers), icon: <ShieldCheck className="w-4 h-4" />, color: '#4ade80' },
            { label: 'Combined GMV', value: fmt(totalGmv), icon: <DollarSign className="w-4 h-4" />, color: '#4ade80' },
            { label: 'Total Reach', value: fmtFollowers(totalFollowers), icon: <TrendingUp className="w-4 h-4" />, color: '#a78bfa' },
          ].map(k => (
            <div key={k.label} className="p-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 rounded" style={{ backgroundColor: `${k.color}10`, color: k.color }}>{k.icon}</div>
              </div>
              <p className="text-2xl font-bold text-white font-display">{k.value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{k.label}</p>
            </div>
          ))}
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.2)' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, handle, or industry..."
              className="w-full pl-9 pr-4 py-2.5 text-sm rounded"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
            />
          </div>
          <div className="flex gap-1.5">
            {([
              ['all', 'All'],
              ['active', 'Active'],
              ['needs_setup', 'Needs Setup'],
              ['inactive', 'Inactive'],
            ] as [Filter, string][]).map(([f, label]) => (
              <button key={f} onClick={() => setFilter(f)}
                className="px-3 py-2 text-xs rounded transition-colors"
                style={filter === f
                  ? { backgroundColor: '#FFD400', color: '#000', fontWeight: 600 }
                  : { backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.06)' }
                }>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 text-sm rounded" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
            {error}
          </div>
        )}

        {/* Influencer list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FFD400' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'rgba(255,255,255,0.25)' }}>
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{search ? 'No influencers match your search.' : 'No influencers yet. Add one to get started.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(inf => (
              <InfluencerCard
                key={inf.id}
                inf={inf}
                expanded={expandedId === inf.id}
                onToggle={() => setExpandedId(expandedId === inf.id ? null : inf.id)}
                onManage={() => openManageModal(inf)}
                onToggleStatus={() => handleToggleStatus(inf)}
                onDelete={() => openDeleteModal(inf)}
                isDemoMode={isDemoMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Create Modal ────────────────────────────────────────────────────── */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setIdManuallyEdited(false); setSlugManuallyEdited(false); }} title="Add New Influencer">
        <form onSubmit={handleCreateInfluencer} className="space-y-4">
          {formError && (
            <div className="p-3 rounded text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>First Name *</label>
              <input type="text" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} className="input w-full" required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Last Name *</label>
              <input type="text" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} className="input w-full" required />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Stage Name *</label>
            <input type="text" value={formData.stage_name}
              onChange={e => {
                const stageName = e.target.value;
                const derived = stageName.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                setFormData({
                  ...formData, stage_name: stageName,
                  influencer_id: idManuallyEdited ? formData.influencer_id : derived,
                  slug: slugManuallyEdited ? formData.slug : derived,
                });
              }}
              className="input w-full" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Influencer ID <span className="opacity-50">(auto)</span></label>
              <input type="text" value={formData.influencer_id} onChange={e => { setIdManuallyEdited(true); setFormData({ ...formData, influencer_id: e.target.value }); }} className="input w-full font-mono text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Slug <span className="opacity-50">(auto)</span></label>
              <input type="text" value={formData.slug} onChange={e => { setSlugManuallyEdited(true); setFormData({ ...formData, slug: e.target.value }); }} className="input w-full font-mono text-xs" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Email (optional)</label>
            <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="input w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Status</label>
            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })} className="input w-full">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={formLoading} className="btn-primary">
              {formLoading ? 'Creating...' : 'Create Influencer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Manage Login Modal ──────────────────────────────────────────────── */}
      <Modal isOpen={showManageModal} onClose={() => { setShowManageModal(false); setSelectedInfluencer(null); }} title={`Manage Login: ${selectedInfluencer?.stage_name || ''}`}>
        {selectedInfluencer && (
          <div className="space-y-5">
            {manageError && (
              <div className="p-3 rounded text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
                {manageError}
              </div>
            )}
            {manageSuccess && (
              <div className="p-3 rounded text-sm" style={{ backgroundColor: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.18)', color: '#4ade80' }}>
                {manageSuccess}
              </div>
            )}
            {inviteResult && (
              <div className="p-4 rounded" style={{ backgroundColor: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.18)' }}>
                <p className="text-sm font-semibold mb-2" style={{ color: '#4ade80' }}>Invite sent</p>
                {inviteResult.url && (
                  <div className="flex gap-2 mb-2">
                    <input type="text" value={inviteResult.url} readOnly className="input flex-1 font-mono text-xs" />
                    <button onClick={() => copyToClipboard(inviteResult.url!)} className="btn-secondary p-2"><Copy className="w-4 h-4" /></button>
                  </div>
                )}
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Expires: {formatDate(inviteResult.expiresAt)}</p>
              </div>
            )}
            {resetResult && (
              <div className="p-4 rounded" style={{ backgroundColor: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.18)' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#4ade80' }}>Password reset email sent</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{resetResult}</p>
              </div>
            )}

            <div className="p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Account Status</span>
                <StatusBadge variant={
                  selectedInfluencer.has_user && selectedInfluencer.has_password ? 'success'
                    : selectedInfluencer.has_user ? 'warning' : 'neutral'
                }>
                  {selectedInfluencer.has_user && selectedInfluencer.has_password ? 'Active'
                    : selectedInfluencer.has_user ? 'Invited' : 'No Account'}
                </StatusBadge>
              </div>
              {selectedInfluencer.has_user && (
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.2)' }}>Current email: {selectedInfluencer.user_email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Email Address</label>
              <input type="email" value={loginEmail}
                onChange={e => { setLoginEmail(e.target.value); setEmailEdited(e.target.value !== (selectedInfluencer.user_email || selectedInfluencer.email || '')); }}
                className="input w-full" placeholder="influencer@example.com" />
            </div>

            <div className="space-y-2">
              {selectedInfluencer.has_user && emailEdited && loginEmail && (
                <button onClick={handleUpdateEmail} disabled={manageLoading} className="btn-secondary w-full text-sm">
                  {manageLoading ? 'Updating...' : 'Save Email Change'}
                </button>
              )}
              <button onClick={handleSendInvite} disabled={manageLoading || !loginEmail} className="btn-primary w-full text-sm disabled:opacity-50">
                {manageLoading ? 'Sending...' : selectedInfluencer.has_user ? 'Re-send Invite Email' : 'Send Invite Email'}
              </button>
              {selectedInfluencer.has_user && selectedInfluencer.has_password && !resetResult && (
                <button onClick={handleResetPassword} disabled={manageLoading} className="btn-ghost w-full text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  Send Password Reset Email Instead
                </button>
              )}
            </div>
            <p className="text-[10px] text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>Invite links expire in 48 hours.</p>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ───────────────────────────────────────── */}
      <Modal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setInfluencerToDelete(null); }} title="Delete Influencer">
        {influencerToDelete && (
          <div className="space-y-5">
            {deleteError && (
              <div className="p-3 rounded text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
                {deleteError}
              </div>
            )}
            <div className="p-4 rounded" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)' }}>
              <p className="text-sm font-semibold mb-1" style={{ color: '#f87171' }}>This action cannot be undone</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Delete <strong className="text-white">{influencerToDelete.stage_name}</strong> ({influencerToDelete.first_name} {influencerToDelete.last_name})?
              </p>
            </div>
            <ul className="text-xs space-y-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
              <li>• Deletes the influencer profile</li>
              {influencerToDelete.has_user && <li>• Deletes their user account and credentials</li>}
              <li>• Historical orders preserved (shown as unattributed)</li>
            </ul>
            <div className="flex gap-3">
              <button onClick={() => { setShowDeleteModal(false); setInfluencerToDelete(null); }} className="btn-secondary flex-1" disabled={deleteLoading}>Cancel</button>
              <button onClick={handleDeleteInfluencer} disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold rounded transition-colors"
                style={{ backgroundColor: '#f87171', color: '#fff' }}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}
